// Project table columns — the domain cells (name tile/badge/sub-line, owner,
// status/priority chips, dates + OVERDUE, tags, row kebab) rendered inside the
// common DataTable. Structure/selection/sorting belong to DataTable itself.

import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Icon, type ChipVariant, type DataTableColumn } from '@/components/common';
import type {
  ProjectListItem,
  ProjectPriority,
  ProjectState,
  ProjectStatus,
} from '@/api/endpoints/projectsApi';
import { fmtDate } from '@/utils/formatDate';
import ProjectRowActions from './ProjectRowActions';
import initialsOf from '@/utils/initials';

// One display chip covers BOTH backend columns — lifecycle `status`
// (active/archived) and operational `state` (active/inactive): archived wins,
// otherwise the state shows.
export type ProjectDisplayStatus = ProjectStatus | ProjectState;
export const displayStatus = (
  p: Pick<ProjectListItem, 'status' | 'state'>,
): ProjectDisplayStatus => (p.status === 'archived' ? 'archived' : p.state);

// Total over the display union (active | inactive | archived), so no fallback
// is needed — Archived-tab rows carry 'archived' and get the quiet outline
// chip rather than reading as live.
export const STATUS_META: Record<ProjectDisplayStatus, { chip: ChipVariant; icon: string }> = {
  active: { chip: 'success', icon: 'circle-check' },
  inactive: { chip: 'neutral', icon: 'circle-pause' },
  archived: { chip: 'outline', icon: 'archive' },
};

// Tones match the project drawer's priority pills (low = info, medium =
// warning, high = danger) so a project reads the same everywhere. Chip has no
// tier above danger, so 'urgent' shares high's tone and is told apart by its
// own icon — the drawer's Urgent pill uses that same icon.
export const PRIORITY_META: Record<
  ProjectPriority,
  { chip: 'info' | 'warning' | 'danger'; icon: string }
> = {
  low: { chip: 'info', icon: 'chevron-down' },
  medium: { chip: 'warning', icon: 'equal' },
  high: { chip: 'danger', icon: 'chevrons-up' },
  urgent: { chip: 'danger', icon: 'signal' },
};

export interface UseProjectColumnsOptions {
  archivedTab: boolean;
  onOpen: (project: ProjectListItem) => void;
  onEdit: (project: ProjectListItem) => void;
  onArchiveRestore: (project: ProjectListItem) => void;
  onDelete: (project: ProjectListItem) => void;
  canEdit: boolean;
  canArchiveRestore: boolean;
  /** The `delete` verb AND the Archived tab — a live project can only be
   *  archived; deleting one is reachable only after it has been. */
  canDelete: boolean;
}

export const useProjectColumns = ({
  archivedTab,
  onOpen,
  onEdit,
  onArchiveRestore,
  onDelete,
  canEdit,
  canArchiveRestore,
  canDelete,
}: UseProjectColumnsOptions): DataTableColumn<ProjectListItem>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'title',
      label: t('projects_col_name'),
      sortable: true,
      // A fixed floor (not `auto`) lets the cell shrink to the track, so long
      // titles ellipsize through the `min-width: 0` chain (inspections idiom).
      width: 'minmax(16rem, 1.6fr)',
      render: (project) => (
        <span className='projects-table__name-cell'>
          <span
            className={`projects-table__tile${
              project.isDefault ? ' projects-table__tile--default' : ''
            }`}
          >
            <Icon name={project.icon} size={20} strokeWidth={2} />
          </span>
          <span className='projects-table__label'>
            <span className='projects-table__name'>
              <span className='projects-table__name-text' title={project.name}>
                {project.name}
              </span>
              {project.isDefault && (
                <span className='projects-table__badge'>{t('projects_default_badge')}</span>
              )}
            </span>
            <span className='projects-table__sub'>
              {project.isDefault ? (
                <>
                  <Icon name={project.everyoneAccess ? 'globe' : 'lock'} size={11} />
                  {project.everyoneAccess
                    ? t('projects_access_all')
                    : t('projects_access_restricted')}
                </>
              ) : (
                <>
                  <Icon name='users' size={11} />
                  {t('projects_members', { count: project.membersCount })}
                </>
              )}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: 'owner',
      label: t('projects_col_owner'),
      render: (project) => (
        <span className='projects-table__owner'>
          <Avatar initials={initialsOf(project.owner.name)} size={30} />
          {project.owner.name || '—'}
        </span>
      ),
    },
    {
      key: 'state',
      label: t('projects_col_status'),
      sortable: true,
      // The column shows the operational `state` only (Active/Inactive) — the
      // tab already says whether a row is archived.
      render: (project) => {
        const meta = STATUS_META[project.state];
        return (
          <Chip variant={meta.chip} iconStart={meta.icon}>
            {t(`projects_status_${project.state}`)}
          </Chip>
        );
      },
    },
    {
      key: 'priority',
      label: t('projects_col_priority'),
      sortable: true,
      render: (project) => {
        const priority = PRIORITY_META[project.priority];
        return (
          <Chip variant={priority.chip} iconStart={priority.icon}>
            {t(`projects_priority_${project.priority}`)}
          </Chip>
        );
      },
    },
    {
      key: 'startDate',
      label: t('projects_col_start'),
      sortable: true,
      render: (project) => (
        <span className='projects-table__date'>{fmtDate(project.startDate)}</span>
      ),
    },
    {
      key: 'dueDate',
      label: t('projects_col_end'),
      sortable: true,
      render: (project) => {
        const overdue =
          !archivedTab && !!project.endDate && new Date(project.endDate).getTime() < Date.now();
        return (
          <span className={`projects-table__date${overdue ? ' is-overdue' : ''}`}>
            {fmtDate(project.endDate)}
            {overdue && <b className='projects-table__overdue'>{t('projects_overdue')}</b>}
          </span>
        );
      },
    },
    {
      key: 'tags',
      label: t('projects_col_tags'),
      render: (project) => {
        const shown = project.tags.slice(0, 2);
        const extra = project.tags.length - shown.length;
        return (
          <span className='projects-table__tags'>
            {project.tags.length === 0 ? (
              <span className='projects-table__dash'>—</span>
            ) : (
              <>
                {shown.map((tag) => (
                  <Chip key={tag.id} variant='neutral'>
                    {tag.title}
                  </Chip>
                ))}
                {extra > 0 && <span className='projects-table__more'>+{extra}</span>}
              </>
            )}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: t('projects_col_actions'),
      align: 'end',
      width: '4rem',
      // Write items are passed only when the capability is granted AND the
      // row isn't a pinned default — system projects get Open only.
      render: (project) => (
        <ProjectRowActions
          archived={archivedTab}
          onOpen={() => onOpen(project)}
          onEdit={canEdit && !project.isDefault ? () => onEdit(project) : undefined}
          onArchiveRestore={
            canArchiveRestore && !project.isDefault ? () => onArchiveRestore(project) : undefined
          }
          onDelete={canDelete && !project.isDefault ? () => onDelete(project) : undefined}
        />
      ),
    },
  ];
};
